// @ts-nocheck
import { valueOrDefault } from "chart.js/helpers";
import { ActiveElement, Chart, ChartEvent, Plugin, TooltipModel } from "chart.js";

import { isFiniteNumber } from "../../../../utils/number";

const defaultOptions = {
  line: {
    color: "#F66",
    width: 1,
    dashPattern: [],
  },
};

type SupportedType = "line" | "radar";

type CrosshairState = {
  enabled: boolean;
  x: number | null;
  originalData: unknown[];
  originalXRange: Record<string, unknown>;
  ignoreNextEvents: number;
  locked: boolean;
  lockedX: number | null;
  lockedActiveElements: ActiveElement<SupportedType>[] | null;
  snappedActiveElements: ActiveElement<SupportedType>[] | null;
  snapPixelY: number | null;
  baseValue: number | null;
  baseDatasetIndex: number | null;
  markerValue: number | null;
  markerDatasetIndex: number | null;
  syncCursorValues?: readonly (number | null)[] | null;
  syncMarkerValues?: readonly (number | null)[] | null;
  syncDatasetRanges?: readonly { datasetIndex: number; min: number; max: number }[] | null;
  syncMarkerInRange?: boolean;
  syncedPoint?: { datasetIndex: number; nativeX: number; x: number; y: number } | null;
  syncBand?: { datasetIndex: number; min: number; max: number } | null;
  unlockedOnClick: boolean;
  keyboardNavigationEnabled: boolean;
};

type ChartType = Chart<SupportedType> & {
  crosshair: CrosshairState;
  _lockedTooltip?: TooltipModel<SupportedType> | null;
};

type SnapResult = {
  crosshairX: number;
  activeElements: ActiveElement<SupportedType>[] | null;
};

type TooltipController = TooltipModel<SupportedType> & {
  setActiveElements?(active: ActiveElement<SupportedType>[], eventPosition: { x: number; y: number }): void;
  _active?: ActiveElement<SupportedType>[];
  update?(change?: boolean): void;
};

type CrosshairPluginOptions = {
  onCursorClick?: (chart: ChartType) => void;
  onKeyboardStep?: (direction: -1 | 1) => boolean;
  keyboardNavigationEnabled?: boolean;
  markerMode?: boolean;
  markerLine?: {
    color?: string;
    width?: number;
    dashPattern?: number[];
  };
  xOffset?: number;
};

const applyTooltipActiveElements = (
  chart: ChartType,
  activeElements: ActiveElement<SupportedType>[] | null,
  position: { x: number; y: number },
): void => {
  const tooltip = chart.tooltip as TooltipController;
  if (!tooltip || !activeElements || activeElements.length === 0) {
    return;
  }

  if (typeof chart.setActiveElements === "function") {
    chart.setActiveElements(chart.crosshair?.syncedPoint ? [] : activeElements);
  }

  if (typeof tooltip.setActiveElements === "function") {
    tooltip.setActiveElements(activeElements, position);
  } else {
    tooltip._active = activeElements;
  }
  if (typeof tooltip.update === "function") {
    tooltip.update(true);
  }
};

const clearActiveElements = (chart: ChartType, position: { x: number; y: number }): void => {
  if (typeof chart.setActiveElements === "function") {
    chart.setActiveElements([]);
  }

  const tooltip = chart.tooltip as TooltipController;
  if (!tooltip) {
    return;
  }

  if (typeof tooltip.setActiveElements === "function") {
    tooltip.setActiveElements([], position);
  } else {
    tooltip._active = [];
  }

  if (typeof tooltip.update === "function") {
    tooltip.update(true);
  }
};

type SelectionZoomState = {
  mouseDown?: boolean;
  dragging?: boolean;
  suppressClickLock?: number;
};

type ChartPointElement = { x?: number; y?: number } | null | undefined;

type ExtendedActiveElement = {
  datasetIndex: number;
  index: number;
  element?: ChartPointElement;
  pixelX: number;
  pixelY: number;
  pixelDistanceSq: number;
  dataX: number;
};

type ChartAxisScale = {
  min: number;
  max: number;
  getValueForPixel: (pixel: number) => number;
  getPixelForValue: (value: number) => number;
};

type DataPoint = {
  x: number;
  y: number;
};

type ChartArea = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

const getDataPoint = (point: unknown): DataPoint | null => {
  if (!point || typeof point !== "object") {
    return null;
  }

  const candidate = point as Record<string, unknown>;

  return isFiniteNumber(candidate.x) && isFiniteNumber(candidate.y) ? { x: candidate.x, y: candidate.y } : null;
};

const resolveTargetPixelY = (crosshair: CrosshairState, approxPixelY?: number | null): number | null => {
  if (isFiniteNumber(approxPixelY)) {
    return approxPixelY;
  }

  return isFiniteNumber(crosshair.snapPixelY) ? crosshair.snapPixelY : null;
};

const isPointInsideChartArea = (chartArea: ChartArea | undefined, pixelX: number, pixelY: number): boolean => {
  if (!chartArea) {
    return true;
  }

  return pixelX >= chartArea.left && pixelX <= chartArea.right && pixelY >= chartArea.top && pixelY <= chartArea.bottom;
};

const getAxisScale = (chart: ChartType, axisId: string): ChartAxisScale | null => {
  const scales = chart.scales as Record<string, ChartAxisScale | undefined>;
  const scale = scales[axisId] ?? scales.x;
  if (!scale || typeof scale.getValueForPixel !== "function" || typeof scale.getPixelForValue !== "function") {
    return null;
  }
  return scale;
};

const updateSyncedPixels = (chart: ChartType): void => {
  const crosshair = chart.crosshair;
  const point = crosshair?.syncedPoint;
  if (!crosshair?.locked || !point) {
    return;
  }
  const meta = chart.getDatasetMeta(point.datasetIndex);
  const xScale = getAxisScale(chart, meta.xAxisID);
  const yScale = getAxisScale(chart, meta.yAxisID);
  if (!xScale || !yScale || meta.hidden) {
    return;
  }
  const x = xScale.getPixelForValue(point.x);
  const y = yScale.getPixelForValue(point.y);
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
    return;
  }
  crosshair.x = x;
  crosshair.lockedX = x;
  crosshair.snapPixelY = y;
};

const isEditableElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
};

export default {
  id: "crosshair",

  beforeEvent: function (chart: ChartType, args: { event: ChartEvent; replay?: boolean }): void | false {
    if (chart.options?.plugins?.crosshair === false) {
      return;
    }

    const selectionZoom = (chart as ChartType & { selectionZoom?: SelectionZoomState }).selectionZoom;
    if (selectionZoom?.dragging) {
      return;
    }

    const e = args.event as ChartEvent & { native?: MouseEvent };
    if (!e) return;

    const isLeftClick =
      e.native?.type === "click" && (!e.native || typeof e.native.button !== "number" || e.native.button === 0);

    if (chart.crosshair?.syncCursorValues && !chart.crosshair.syncedPoint && !isLeftClick) {
      const type = e.native?.type ?? e.type;
      if (!selectionZoom?.mouseDown && (type === "mousemove" || type === "mouseout" || type === "mouseleave")) {
        return false;
      }
      return;
    }

    const crosshairOptions = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair ?? {};
    if (
      isLeftClick &&
      crosshairOptions.markerMode &&
      chart.crosshair &&
      !isFiniteNumber(chart.crosshair.markerValue) &&
      chart.config.type !== "radar"
    ) {
      if (selectionZoom?.suppressClickLock && selectionZoom.suppressClickLock > 0) {
        return;
      }

      const xScale = this.getXScale(chart);
      if (xScale && isFiniteNumber(e.x)) {
        const minPx = xScale.getPixelForValue(xScale.min);
        const maxPx = xScale.getPixelForValue(xScale.max);

        if (e.x > Math.min(minPx, maxPx) && e.x < Math.max(minPx, maxPx)) {
          chart._lockedTooltip = null;
          chart.crosshair.locked = false;
          chart.crosshair.lockedX = null;
          chart.crosshair.lockedActiveElements = null;
          chart.crosshair.snapPixelY = isFiniteNumber(e.y) ? e.y : null;

          const snapped = this.persistSnapAtPixel(chart, e.x, e.y);
          if (snapped.activeElements?.length && isFiniteNumber(chart.crosshair.baseValue)) {
            chart.crosshair.markerValue = chart.crosshair.baseValue;
            chart.crosshair.markerDatasetIndex = chart.crosshair.baseDatasetIndex;
          }

          chart.crosshair.enabled = true;
          (e as any).x = snapped.crosshairX;

          const snappedElement = snapped.activeElements?.[0]?.element;
          if (snappedElement && typeof snappedElement.y === "number") {
            (e as any).y = snappedElement.y;
          }
          if (!args.replay && snapped.activeElements?.length) {
            crosshairOptions.onCursorClick?.(chart);
          }
        }
      }

      return;
    }

    if (isLeftClick && chart.crosshair && chart.config.type !== "radar") {
      if (selectionZoom?.suppressClickLock && selectionZoom.suppressClickLock > 0) {
        return;
      }

      if (chart.crosshair.locked) {
        chart._lockedTooltip = null;
        chart.crosshair.locked = false;
        chart.crosshair.lockedX = null;
        chart.crosshair.lockedActiveElements = null;
        chart.crosshair.unlockedOnClick = true;

        const xScale = this.getXScale(chart);
        if (xScale && isFiniteNumber(e.x)) {
          const minPx = xScale.getPixelForValue(xScale.min);
          const maxPx = xScale.getPixelForValue(xScale.max);

          if (e.x > minPx && e.x < maxPx) {
            chart.crosshair.snapPixelY = isFiniteNumber(e.y) ? e.y : null;

            const snapped = this.persistSnapAtPixel(chart, e.x, e.y);

            chart.crosshair.enabled = true;

            (e as any).x = snapped.crosshairX;

            const snappedElement = snapped.activeElements?.[0]?.element;
            if (snappedElement && typeof snappedElement.y === "number") {
              (e as any).y = snappedElement.y;
            }

            if (!args.replay && snapped.activeElements?.length) {
              crosshairOptions.onCursorClick?.(chart);
            }

            return;
          }
        }

        chart.crosshair.snappedActiveElements = null;
        chart.crosshair.enabled = false;
        chart.crosshair.x = null;
        clearActiveElements(chart, {
          x: isFiniteNumber(e.x) ? e.x : 0,
          y: isFiniteNumber(e.y) ? e.y : 0,
        });
        return;
      }

      const xScale = this.getXScale(chart);
      if (xScale && isFiniteNumber(e.x)) {
        const minPx = xScale.getPixelForValue(xScale.min);
        const maxPx = xScale.getPixelForValue(xScale.max);

        if (e.x > minPx && e.x < maxPx) {
          chart._lockedTooltip = null;
          chart.crosshair.snapPixelY = isFiniteNumber(e.y) ? e.y : null;

          const snapped = this.persistSnapAtPixel(chart, e.x, e.y);

          chart.crosshair.locked = true;
          chart.crosshair.lockedX = snapped.crosshairX;
          chart.crosshair.lockedActiveElements = snapped.activeElements;
          chart.canvas.focus();

          chart.crosshair.enabled = true;

          (e as any).x = snapped.crosshairX;

          const snappedElement = snapped.activeElements?.[0]?.element;
          if (snappedElement && typeof snappedElement.y === "number") {
            (e as any).y = snappedElement.y;
          }
          if (!args.replay && snapped.activeElements?.length) {
            crosshairOptions.onCursorClick?.(chart);
          }
        }
      }

      return;
    }

    const crosshair = chart.crosshair;
    if (!crosshair) {
      return;
    }

    if (selectionZoom?.mouseDown && e.native?.type === "mousemove") return;

    if (
      !crosshair.locked &&
      chart.config.type !== "radar" &&
      e.type !== "mouseout" &&
      e.type !== "mouseleave" &&
      isFiniteNumber(e.x)
    ) {
      const xScale = this.getXScale(chart);
      if (xScale) {
        const minPx = xScale.getPixelForValue(xScale.min);
        const maxPx = xScale.getPixelForValue(xScale.max);

        const chartArea = chart.chartArea;
        const withinChartArea =
          chartArea &&
          isFiniteNumber(e.x) &&
          isFiniteNumber(e.y) &&
          e.x >= chartArea.left &&
          e.x <= chartArea.right &&
          e.y >= chartArea.top &&
          e.y <= chartArea.bottom;

        if (e.x > minPx && e.x < maxPx && withinChartArea) {
          crosshair.snapPixelY = isFiniteNumber(e.y) ? e.y : null;

          const snapped = this.persistSnapAtPixel(chart, e.x, e.y);

          crosshair.enabled = true;

          (e as any).x = snapped.crosshairX;

          const snappedElement = snapped.activeElements?.[0]?.element;
          if (snappedElement && typeof snappedElement.y === "number") {
            (e as any).y = snappedElement.y;
          }
        } else {
          crosshair.enabled = false;
        }
      }
    }

    if (!crosshair.locked || crosshair.lockedX == null) {
      return;
    }

    if (e.native?.type === "mousedown" && e.native.button === 0) {
      return;
    }

    const lockedX = crosshair.lockedX;
    (e as any).x = lockedX;

    if (crosshair.lockedActiveElements && crosshair.lockedActiveElements.length > 0) {
      const first = crosshair.lockedActiveElements[0];
      const element = (first as any).element || first;
      if (element && typeof element.y === "number") {
        const lockedY = element.y;
        (e as any).y = lockedY;
      }
    }
  },

  afterInit: function (chart: ChartType) {
    if (chart.options?.plugins?.crosshair === false) {
      return;
    }

    if (!chart.config.options?.scales?.x) {
      return;
    }

    const xScaleType = chart.config.options.scales.x.type;

    if (xScaleType !== "linear" && xScaleType !== "time" && xScaleType !== "category" && xScaleType !== "logarithmic") {
      return;
    }

    if (chart.options?.plugins?.crosshair === undefined) {
      chart.options.plugins!.crosshair = defaultOptions;
    }

    chart.crosshair = {
      enabled: false,
      x: null,
      originalData: [],
      originalXRange: {},
      ignoreNextEvents: 0,
      locked: false,
      lockedX: null,
      lockedActiveElements: null,
      snappedActiveElements: null,
      snapPixelY: null,
      baseValue: null,
      baseDatasetIndex: null,
      markerValue: null,
      markerDatasetIndex: null,
      unlockedOnClick: false,
      keyboardNavigationEnabled: true,
    };

    chart.crosshair.keyboardNavigationEnabled =
      ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions })?.crosshair
        ?.keyboardNavigationEnabled !== false;

    chart.canvas.tabIndex = chart.canvas.tabIndex >= 0 ? chart.canvas.tabIndex : 0;

    const keyHandler = (event: KeyboardEvent) => {
      if (!chart.crosshair?.locked || !chart.crosshair.keyboardNavigationEnabled) {
        return;
      }

      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      if (isEditableElement(event.target)) {
        return;
      }

      if (document.activeElement !== chart.canvas) {
        return;
      }

      const step = event.key === "ArrowRight" ? 1 : -1;
      const keyboardStep = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair
        ?.onKeyboardStep;
      const moved = keyboardStep ? keyboardStep(step) === true : this.moveLockedCrosshairByStep(chart, step);
      if (!moved) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    chart.canvas.addEventListener("keydown", keyHandler);
    (chart as ChartType & { _crosshairKeyHandler?: (event: KeyboardEvent) => void })._crosshairKeyHandler = keyHandler;
  },

  beforeUpdate: function (chart: ChartType) {
    const options = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair;
    if (chart.crosshair && (options === false || !options?.markerMode)) {
      chart.crosshair.markerValue = null;
      chart.crosshair.markerDatasetIndex = null;
      chart.crosshair.syncMarkerValues = null;
      chart.crosshair.syncBand = null;
    }
  },

  beforeDestroy: function (chart: ChartType) {
    const keyHandler = (chart as ChartType & { _crosshairKeyHandler?: (event: KeyboardEvent) => void })
      ._crosshairKeyHandler;
    if (keyHandler) {
      chart.canvas.removeEventListener("keydown", keyHandler);
    }
  },

  afterLayout: updateSyncedPixels,

  getOption: function (chart: ChartType, category: string, name: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return valueOrDefault(
      chart.options.plugins.crosshair[category] ? chart.options.plugins.crosshair[category][name] : undefined,
      defaultOptions[category][name],
    );
  },

  getXScale: function (chart: ChartType): ChartAxisScale | null {
    if (!chart.data.datasets.length) {
      return null;
    }
    return getAxisScale(chart, chart.getDatasetMeta(0).xAxisID);
  },
  getYScale: function (chart: ChartType): ChartAxisScale | null {
    return getAxisScale(chart, chart.getDatasetMeta(0).yAxisID);
  },

  //  Ищет ближайший ПИК в окрестности текущего положения курсора по оси X.
  findNearestElementInDataset: function (
    chart: ChartType,
    datasetIndex: number,
    approxPixelX: number,
    approxPixelY?: number | null,
  ): ExtendedActiveElement | null {
    const dataset: any = chart.data.datasets[datasetIndex];
    const meta: any = chart.getDatasetMeta(datasetIndex);
    if (!dataset || !meta || meta.hidden) return null;

    const xScale = getAxisScale(chart, meta.xAxisID);
    const yScale = getAxisScale(chart, meta.yAxisID);
    if (!xScale || !yScale) {
      return null;
    }

    const data: any[] = Array.isArray(dataset.data) ? (dataset.data as any[]) : [];
    if (data.length === 0) return null;

    const targetPixelY = resolveTargetPixelY(chart.crosshair, approxPixelY);
    const useYDistance = targetPixelY !== null;
    const chartArea = chart.chartArea;

    let nearest: ExtendedActiveElement | null = null;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;

    for (let i = 0; i < data.length; i++) {
      const pt = getDataPoint(data[i]);
      if (!pt) {
        continue;
      }

      const pixelX = xScale.getPixelForValue(pt.x);
      const pixelY = yScale.getPixelForValue(pt.y);

      if (!isFiniteNumber(pixelX) || !isFiniteNumber(pixelY)) {
        continue;
      }

      if (!isPointInsideChartArea(chartArea, pixelX, pixelY)) {
        continue;
      }

      const dx = pixelX - approxPixelX;
      const dy = useYDistance ? pixelY - targetPixelY : 0;
      const distanceSq = dx * dx + dy * dy;

      if (distanceSq < nearestDistanceSq) {
        nearestDistanceSq = distanceSq;
        nearest = {
          datasetIndex,
          index: i,
          element: (meta.data && meta.data[i]) as ChartPointElement,
          pixelX,
          pixelY,
          pixelDistanceSq: distanceSq,
          dataX: pt.x,
        };
      }
    }

    return nearest;
  },

  findNearestElement: function (
    chart: ChartType,
    approxPixelX: number,
    approxPixelY?: number | null,
  ): ExtendedActiveElement | null {
    if (!chart?.data || !Array.isArray(chart.data.datasets)) {
      return null;
    }

    const preferredIndex = chart.crosshair.baseDatasetIndex;
    const datasetIndexes =
      typeof preferredIndex === "number" && preferredIndex >= 0 && preferredIndex < chart.data.datasets.length
        ? [preferredIndex, ...chart.data.datasets.map((_, i) => i).filter(i => i !== preferredIndex)]
        : chart.data.datasets.map((_, i) => i);

    let bestResult: ExtendedActiveElement | null = null;
    let bestPixelDistanceSq = Number.POSITIVE_INFINITY;

    for (const datasetIndex of datasetIndexes) {
      const candidate = this.findNearestElementInDataset(chart, datasetIndex, approxPixelX, approxPixelY);
      if (!candidate) {
        continue;
      }

      if (candidate.pixelDistanceSq < bestPixelDistanceSq) {
        bestPixelDistanceSq = candidate.pixelDistanceSq;
        bestResult = candidate;
      }
    }

    return bestResult;
  },

  snapCrosshairToPixel: function (chart: ChartType, pixelX: number, pixelY?: number | null): SnapResult {
    chart.crosshair.syncedPoint = null;
    chart.crosshair.syncCursorValues = null;
    chart.crosshair.syncBand = null;
    let crosshairX = pixelX;
    let activeElements: ActiveElement<SupportedType>[] | null = null;

    if (!isFiniteNumber(pixelX)) {
      return { crosshairX: pixelX, activeElements: null };
    }

    const nearest = this.findNearestElement(chart, pixelX, pixelY);
    if (nearest) {
      crosshairX = nearest.pixelX;
      chart.crosshair.x = crosshairX;

      const metaForBase = chart.getDatasetMeta(nearest.datasetIndex);
      const xScaleForBase = getAxisScale(chart, metaForBase.xAxisID);
      if (xScaleForBase) {
        const plugins = (chart.options?.plugins || {}) as { crosshair?: { xOffset?: number } };
        const renderedValue =
          typeof nearest.dataX === "number"
            ? nearest.dataX
            : typeof xScaleForBase.getValueForPixel === "function"
              ? xScaleForBase.getValueForPixel(crosshairX)
              : null;
        const crosshairOpts = (plugins.crosshair || {}) as { xOffset?: number };
        const xOffset = crosshairOpts.xOffset ?? 0;
        const baseValue = typeof renderedValue === "number" ? renderedValue - nearest.datasetIndex * xOffset : null;

        chart.crosshair.baseDatasetIndex = nearest.datasetIndex;
        if (typeof baseValue === "number" && Number.isFinite(baseValue)) {
          chart.crosshair.baseValue = baseValue;
        }
      }

      activeElements = [
        {
          datasetIndex: nearest.datasetIndex,
          index: nearest.index,
          element: nearest.element,
        } as ActiveElement<SupportedType>,
      ];
    } else {
      chart.crosshair.x = crosshairX;
    }

    return { crosshairX, activeElements };
  },

  persistSnapAtPixel: function (chart: ChartType, pixelX: number, pixelY?: number | null): SnapResult {
    const result = this.snapCrosshairToPixel(chart, pixelX, pixelY);
    chart.crosshair.x = result.crosshairX;
    chart.crosshair.snappedActiveElements = result.activeElements;
    return {
      crosshairX: result.crosshairX,
      activeElements: result.activeElements,
    };
  },

  moveLockedCrosshairByStep: function (chart: ChartType, step: -1 | 1): boolean {
    const locked = chart.crosshair.lockedActiveElements?.[0];
    const fallbackDatasetIndex = chart.crosshair.baseDatasetIndex;
    const datasetIndex = locked?.datasetIndex ?? fallbackDatasetIndex;
    if (typeof datasetIndex !== "number") {
      return false;
    }

    const dataset = chart.data.datasets[datasetIndex];
    const data = Array.isArray(dataset?.data) ? dataset.data : [];
    if (data.length === 0) {
      return false;
    }

    const lockedIndex = locked?.index;
    let startIndex = typeof lockedIndex === "number" ? lockedIndex : -1;
    if (startIndex < 0 || startIndex >= data.length) {
      const nearest = this.findNearestElementInDataset(
        chart,
        datasetIndex,
        chart.crosshair.lockedX ?? chart.crosshair.x ?? 0,
      );
      startIndex = nearest?.index ?? -1;
    }
    if (startIndex < 0) {
      return false;
    }

    const meta = chart.getDatasetMeta(datasetIndex);
    const xScale = getAxisScale(chart, meta.xAxisID);
    if (!xScale) {
      return false;
    }

    const minPx = Math.min(xScale.getPixelForValue(xScale.min), xScale.getPixelForValue(xScale.max));
    const maxPx = Math.max(xScale.getPixelForValue(xScale.min), xScale.getPixelForValue(xScale.max));

    const crosshairOpts = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair ?? {};
    const xOffset = crosshairOpts.xOffset ?? 0;

    let targetIndex = startIndex;
    while (true) {
      targetIndex += step;
      if (targetIndex < 0 || targetIndex >= data.length) {
        return false;
      }

      const point = getDataPoint(data[targetIndex]);
      if (!point) {
        continue;
      }

      const pointPixelX = xScale.getPixelForValue(point.x);
      if (!isFiniteNumber(pointPixelX) || pointPixelX < minPx || pointPixelX > maxPx) {
        continue;
      }

      const activeElement = {
        datasetIndex,
        index: targetIndex,
        element: (meta.data?.[targetIndex] as ChartPointElement) ?? undefined,
      } as ActiveElement<SupportedType>;
      const activeElements = [activeElement];

      chart.crosshair.baseDatasetIndex = datasetIndex;
      chart.crosshair.baseValue = point.x - datasetIndex * xOffset;
      chart.crosshair.syncedPoint = null;
      chart.crosshair.syncCursorValues = null;
      chart.crosshair.syncBand = null;

      chart.crosshair.snappedActiveElements = activeElements;
      chart.crosshair.lockedX = pointPixelX;
      chart.crosshair.lockedActiveElements = activeElements;
      chart.crosshair.enabled = true;
      chart.crosshair.x = pointPixelX;
      chart.crosshair.snapPixelY = activeElement.element?.y ?? chart.crosshair.snapPixelY;

      applyTooltipActiveElements(chart, activeElements, {
        x: pointPixelX,
        y: activeElement.element?.y ?? chart.chartArea?.top ?? 0,
      });
      chart.update("none");
      return true;
    }
  },

  afterEvent: function (chart: ChartType, event: { event: ChartEvent }): void {
    if (chart.options?.plugins?.crosshair === false) {
      return;
    }

    if (chart.config.type === "radar") {
      return;
    }

    if (chart.config.options.scales.x.length === 0) {
      return;
    }

    const e = event.event as ChartEvent & { native?: MouseEvent };
    const selectionZoom = (chart as ChartType & { selectionZoom?: SelectionZoomState }).selectionZoom;
    if (chart.crosshair?.syncCursorValues && !chart.crosshair.syncedPoint) {
      if (
        selectionZoom?.suppressClickLock &&
        selectionZoom.suppressClickLock > 0 &&
        (e.native?.type === "mouseup" || e.native?.type === "click") &&
        e.native.button === 0
      ) {
        selectionZoom.suppressClickLock -= 1;
      }
      clearActiveElements(chart, { x: isFiniteNumber(e.x) ? e.x : 0, y: isFiniteNumber(e.y) ? e.y : 0 });
      chart.draw();
      return;
    }

    const xScaleType = chart.config.options.scales.x.type;

    if (xScaleType !== "linear" && xScaleType !== "time" && xScaleType !== "category" && xScaleType !== "logarithmic") {
      return;
    }

    const xScale = this.getXScale(chart);

    if (!xScale) {
      return;
    }

    const native = (e.native as MouseEvent | undefined) ?? (e as unknown as MouseEvent);

    const unlockCrosshair = () => {
      chart.crosshair.locked = false;
      chart.crosshair.lockedX = null;
      chart.crosshair.lockedActiveElements = null;
    };

    if (selectionZoom?.dragging) {
      unlockCrosshair();
      chart.crosshair.enabled = false;
      chart.crosshair.x = null;
      clearActiveElements(chart, { x: isFiniteNumber(e.x) ? e.x : 0, y: isFiniteNumber(e.y) ? e.y : 0 });
      chart.draw();
      return;
    }

    if (
      selectionZoom?.suppressClickLock &&
      selectionZoom.suppressClickLock > 0 &&
      native &&
      (native.type === "mouseup" || native.type === "click") &&
      native.button === 0
    ) {
      selectionZoom.suppressClickLock -= 1;
      chart.crosshair.enabled = false;
      chart.crosshair.x = null;
      chart.draw();
      return;
    }

    if (chart.crosshair.unlockedOnClick) {
      chart.crosshair.unlockedOnClick = false;
      chart._lockedTooltip = null;

      const minPx = xScale.getPixelForValue(xScale.min);
      const maxPx = xScale.getPixelForValue(xScale.max);
      const snappedX = chart.crosshair.x;

      chart.crosshair.enabled =
        e.type !== "mouseout" && e.type !== "mouseleave" && snappedX != null && snappedX > minPx && snappedX < maxPx;

      if (chart.crosshair.enabled && chart.crosshair.snappedActiveElements) {
        applyTooltipActiveElements(chart, chart.crosshair.snappedActiveElements, {
          x: snappedX,
          y: chart.crosshair.snappedActiveElements?.[0]?.element?.y ?? native.y,
        });
      } else {
        chart.crosshair.x = null;
        chart.crosshair.snappedActiveElements = null;
        clearActiveElements(chart, {
          x: isFiniteNumber(e.x) ? e.x : 0,
          y: isFiniteNumber(e.y) ? e.y : 0,
        });
      }

      chart.draw();
      return;
    }

    // Фиксируем движение мыши при зафиксированном состоянии
    if (chart.crosshair.locked) {
      chart.crosshair.x = chart.crosshair.lockedX;
      chart.crosshair.enabled = true;

      const isClick = native?.type === "click";
      const isLeftButtonOrUnknown = !native || typeof native.button !== "number" || native.button === 0;

      if (native && isClick && isLeftButtonOrUnknown) {
        applyTooltipActiveElements(chart, chart.crosshair.lockedActiveElements, {
          x: chart.crosshair.lockedX ?? e.x,
          y: chart.crosshair.lockedActiveElements?.[0]?.element?.y ?? native.y,
        });

        chart.draw();
        return;
      }

      if (chart.crosshair.locked && chart.crosshair.lockedActiveElements) {
        applyTooltipActiveElements(chart, chart.crosshair.lockedActiveElements, {
          x: chart.crosshair.lockedX ?? chart.crosshair.lockedActiveElements[0]?.element?.x ?? native.x,
          y: chart.crosshair.lockedActiveElements[0]?.element?.y ?? native.y,
        });
      }
      chart.draw();
      return;
    }

    if (chart.crosshair.ignoreNextEvents > 0) {
      chart.crosshair.ignoreNextEvents -= 1;
      return;
    }

    // fix for Safari
    if (e.native.type === "mouseup") {
      if (e.native.buttons === undefined) {
        e.native.which = 0;
      } else {
        e.native.buttons = 0;
      }
    }

    const minPx = xScale.getPixelForValue(xScale.min);
    const maxPx = xScale.getPixelForValue(xScale.max);
    const snappedX = chart.crosshair.x;

    chart.crosshair.enabled =
      e.type !== "mouseout" && e.type !== "mouseleave" && snappedX != null && snappedX > minPx && snappedX < maxPx;

    if (chart.crosshair.enabled) {
      applyTooltipActiveElements(chart, chart.crosshair.snappedActiveElements, {
        x: snappedX,
        y: chart.crosshair.snappedActiveElements?.[0]?.element?.y ?? native.y,
      });
    } else if (e.type === "mouseout" || e.type === "mouseleave") {
      chart.crosshair.x = null;
      chart.crosshair.snappedActiveElements = null;
      clearActiveElements(chart, {
        x: isFiniteNumber(e.x) ? e.x : 0,
        y: isFiniteNumber(e.y) ? e.y : 0,
      });
    }

    chart.draw();
  },

  afterDraw: function (chart) {
    if (chart.options?.plugins?.crosshair === false) {
      return;
    }

    updateSyncedPixels(chart);
    this.drawMarkerBand(chart);
    this.drawMarkerLine(chart);

    if (!chart.crosshair?.enabled) {
      return;
    }

    this.drawTraceLine(chart);
    this.interpolateValues(chart);
    this.drawTracePoints(chart);
    this.drawSyncedPoint(chart);

    return true;
  },

  getSyncedMarkerDatasetIndex: function (chart: ChartType): number {
    const crosshair = chart.crosshair;
    return (
      crosshair.syncBand?.datasetIndex ??
      crosshair.syncedPoint?.datasetIndex ??
      (crosshair.enabled ? crosshair.baseDatasetIndex : null) ??
      crosshair.markerDatasetIndex ??
      0
    );
  },

  getMarkerPixelX: function (chart: ChartType): number | null {
    const options = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair;
    const crosshair = chart.crosshair;
    if (!options?.markerMode || !crosshair) {
      return null;
    }

    const datasetIndex = crosshair.syncMarkerValues
      ? this.getSyncedMarkerDatasetIndex(chart)
      : crosshair.markerDatasetIndex ?? 0;
    const markerValue = crosshair.syncMarkerValues ? crosshair.syncMarkerValues[datasetIndex] : crosshair.markerValue;
    if (!isFiniteNumber(markerValue)) {
      return null;
    }
    if (crosshair.syncMarkerValues) {
      const range = crosshair.syncDatasetRanges?.find(candidate => candidate.datasetIndex === datasetIndex);
      if (!range || markerValue < range.min || markerValue > range.max) {
        return null;
      }
    }
    const meta = chart.getDatasetMeta(datasetIndex);
    if (crosshair.syncMarkerValues && meta.hidden) {
      return null;
    }
    const xScale = getAxisScale(chart, meta?.xAxisID ?? "x");
    if (!xScale) {
      return null;
    }

    const xOffset = options.xOffset ?? 0;
    const markerPixelX = xScale.getPixelForValue(markerValue + datasetIndex * xOffset);
    return isFiniteNumber(markerPixelX) ? markerPixelX : null;
  },

  drawMarkerBand: function (chart: ChartType) {
    if (chart.crosshair?.syncCursorValues || chart.crosshair?.syncMarkerValues) {
      const crosshair = chart.crosshair;
      let band = crosshair.syncBand;
      if (!crosshair.syncCursorValues) {
        const datasetIndex = this.getSyncedMarkerDatasetIndex(chart);
        const cursorValue = crosshair.baseValue;
        const markerValue = datasetIndex != null ? crosshair.syncMarkerValues?.[datasetIndex] : null;
        const range = crosshair.syncDatasetRanges?.find(candidate => candidate.datasetIndex === datasetIndex);
        if (!crosshair.enabled || !range || !isFiniteNumber(cursorValue) || !isFiniteNumber(markerValue)) {
          return;
        }
        const min = Math.max(range.min, Math.min(cursorValue, markerValue));
        const max = Math.min(range.max, Math.max(cursorValue, markerValue));
        band = min < max ? { datasetIndex, min, max } : null;
      }
      const options = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair;
      if (!options?.markerMode || !band || !chart.chartArea) {
        return;
      }
      const meta = chart.getDatasetMeta(band.datasetIndex);
      const scale = getAxisScale(chart, meta.xAxisID);
      if (!scale) {
        return;
      }
      const offset = band.datasetIndex * (options.xOffset ?? 0);
      const start = scale.getPixelForValue(band.min + offset);
      const end = scale.getPixelForValue(band.max + offset);
      const { left, right, top, bottom } = chart.chartArea;
      const bandLeft = Math.max(left, Math.min(start, end));
      const bandRight = Math.min(right, Math.max(start, end));
      if (bandRight > bandLeft) {
        chart.ctx.save();
        chart.ctx.fillStyle = "rgba(63, 109, 181, 0.08)";
        chart.ctx.fillRect(bandLeft, top, bandRight - bandLeft, bottom - top);
        chart.ctx.restore();
      }
      return;
    }
    const markerPixelX = this.getMarkerPixelX(chart);
    const cursorPixelX = chart.crosshair?.x;
    const chartArea = chart.chartArea;
    if (markerPixelX === null || !chart.crosshair?.enabled || !isFiniteNumber(cursorPixelX) || !chartArea) {
      return;
    }

    const left = Math.max(chartArea.left, Math.min(markerPixelX, cursorPixelX));
    const right = Math.min(chartArea.right, Math.max(markerPixelX, cursorPixelX));
    if (right <= left) {
      return;
    }

    chart.ctx.save();
    chart.ctx.fillStyle = "rgba(63, 109, 181, 0.08)";
    chart.ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    chart.ctx.restore();
  },

  drawMarkerLine: function (chart: ChartType) {
    const options = ((chart.options?.plugins || {}) as { crosshair?: CrosshairPluginOptions }).crosshair;
    const markerPixelX = this.getMarkerPixelX(chart);
    const chartArea = chart.chartArea;
    if (markerPixelX === null || !chartArea || markerPixelX < chartArea.left || markerPixelX > chartArea.right) {
      return;
    }

    chart.ctx.save();
    chart.ctx.beginPath();
    chart.ctx.setLineDash(options.markerLine?.dashPattern ?? []);
    chart.ctx.moveTo(markerPixelX, chartArea.top);
    chart.ctx.lineTo(markerPixelX, chartArea.bottom);
    chart.ctx.lineWidth = options.markerLine?.width ?? 1.5;
    chart.ctx.strokeStyle = options.markerLine?.color ?? "#2e7d32";
    chart.ctx.stroke();
    chart.ctx.restore();
  },

  drawTraceLine: function (chart) {
    const yScale = this.getYScale(chart);
    if (!yScale) {
      return;
    }

    const lineWidth = this.getOption(chart, "line", "width");
    const color = this.getOption(chart, "line", "color");
    const dashPattern = this.getOption(chart, "line", "dashPattern");

    const lineX = chart.crosshair?.x;

    chart.ctx.beginPath();
    chart.ctx.setLineDash(dashPattern);
    chart.ctx.moveTo(lineX, yScale.getPixelForValue(yScale.max));
    chart.ctx.lineWidth = lineWidth;
    chart.ctx.strokeStyle = color;
    chart.ctx.lineTo(lineX, yScale.getPixelForValue(yScale.min));
    chart.ctx.stroke();
    chart.ctx.setLineDash([]);
  },

  drawTracePoints: function (chart) {
    for (let chartIndex = 0; chartIndex < chart.data.datasets.length; chartIndex++) {
      const dataset = chart.data.datasets[chartIndex];
      const meta = chart.getDatasetMeta(chartIndex);

      const yScale = chart.scales[meta.yAxisID];

      if (meta.hidden || !dataset.interpolate) {
        continue;
      }

      chart.ctx.beginPath();
      chart.ctx.arc(chart.crosshair.x, yScale.getPixelForValue(dataset.interpolatedValue), 3, 0, 2 * Math.PI, false);
      chart.ctx.fillStyle = "white";
      chart.ctx.lineWidth = 2;
      chart.ctx.strokeStyle = dataset.borderColor;
      chart.ctx.fill();
      chart.ctx.stroke();
    }
  },

  drawSyncedPoint: function (chart: ChartType) {
    const point = chart.crosshair?.syncedPoint;
    if (!point || !chart.crosshair.locked) {
      return;
    }
    const meta = chart.getDatasetMeta(point.datasetIndex);
    const xScale = getAxisScale(chart, meta.xAxisID);
    const yScale = getAxisScale(chart, meta.yAxisID);
    if (!xScale || !yScale || meta.hidden) {
      return;
    }
    const { left, right, top, bottom } = chart.chartArea;
    chart.ctx.save();
    chart.ctx.beginPath();
    chart.ctx.rect(left, top, right - left, bottom - top);
    chart.ctx.clip();
    chart.ctx.beginPath();
    chart.ctx.arc(xScale.getPixelForValue(point.x), yScale.getPixelForValue(point.y), 7, 0, Math.PI * 2);
    chart.ctx.fillStyle = chart.data.datasets[point.datasetIndex].borderColor;
    chart.ctx.fill();
    chart.ctx.restore();
  },

  interpolateValues: function (chart: Chart<SupportedType>) {
    for (let chartIndex = 0; chartIndex < chart.data.datasets.length; chartIndex++) {
      const dataset = chart.data.datasets[chartIndex];

      const meta = chart.getDatasetMeta(chartIndex);

      const xScale = chart.scales[meta.xAxisID];
      const xValue = xScale.getValueForPixel(chart.crosshair.x);

      if (meta.hidden || !dataset.interpolate) {
        continue;
      }

      const data = dataset.data;
      const index = data.findIndex(function (o) {
        return o.x >= xValue;
      });
      const prev = data[index - 1];
      const next = data[index];

      if (chart.data.datasets[chartIndex].steppedLine && prev) {
        dataset.interpolatedValue = prev.y;
      } else if (prev && next) {
        const slope = (next.y - prev.y) / (next.x - prev.x);
        dataset.interpolatedValue = (prev.y as number) + (xValue - prev.x) * slope;
      } else {
        dataset.interpolatedValue = NaN;
      }
    }
  },
} as Plugin<SupportedType>;
